<template>
  <div class="ai-analysis-report">
    <!-- Login Required Message -->
    <div v-if="!isAuthenticated" class="row">
      <div class="col-12">
        <div class="card border-primary">
          <div class="card-body text-center py-5">
            <div class="mb-4">
              <i class="bi bi-person-lock display-1 text-primary"></i>
            </div>
            <h4 class="card-title mb-3">Login Required</h4>
            <p class="card-text text-muted mb-4">
              Please log in to access your personalized AI health analysis and get intelligent health insights.
            </p>
            <div class="d-flex gap-3 justify-content-center">
              <router-link to="/login" class="btn btn-primary px-4">
                <i class="bi bi-box-arrow-in-right me-2"></i>Login
              </router-link>
              <router-link to="/register" class="btn btn-outline-primary px-4">
                <i class="bi bi-person-plus me-2"></i>Sign Up
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authenticated Content -->
    <div v-else class="row">
      <!-- Main Report Section -->
      <div class="col-lg-8">
        <!-- Header with Generate Button -->
        <div class="card mb-4">
          <div class="card-header bg-gradient-primary text-white d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <i class="bi bi-robot me-2"></i>AI Health Analysis Report
            </h5>
            <div>
              <button
                class="btn btn-light btn-sm me-2"
                @click="generateReport"
                :disabled="generating"
              >
                <span v-if="generating" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="bi bi-magic me-2"></i>
                {{ generating ? 'Generating...' : (report ? 'Regenerate' : 'Generate Report') }}
              </button>
            </div>
          </div>

          <div class="card-body">
            <!-- Loading State -->
            <div v-if="generating" class="text-center py-5">
              <div class="spinner-border text-primary mb-3" role="status"></div>
              <p class="text-muted">Analyzing your health data with AI...</p>
            </div>

            <!-- No Report Yet -->
            <div v-else-if="!report" class="text-center py-5">
              <i class="bi bi-stars display-1 text-muted mb-3"></i>
              <h5 class="text-muted">No AI Analysis Yet</h5>
              <p class="text-muted mb-4">
                Click "Generate Report" to get your personalized health insights
              </p>
              <button class="btn btn-primary btn-lg" @click="generateReport">
                <i class="bi bi-magic me-2"></i>Generate AI Report
              </button>
            </div>

            <!-- AI Report Display -->
            <div v-else>
              <div class="analysis-section mb-4">
                <h6 class="section-title">
                  <i class="bi bi-flower1 me-2"></i>Personal Health Analysis
                </h6>
                <div class="analysis-card">
                  <p>{{ report.phase_analysis }}</p>
                </div>
              </div>

              <div class="analysis-section mb-4">
                <h6 class="section-title">Recommendations</h6>
                
                <div class="recommendation-card mb-3">
                  <h6><i class="bi bi-activity me-2"></i>Exercise Guidance</h6>
                  <ul>
                    <li v-for="(rec, i) in report.exercise_recommendation" :key="i">{{ rec }}</li>
                  </ul>
                </div>

                <div class="recommendation-card mb-3">
                  <h6><i class="bi bi-moon-stars me-2"></i>Energy & Recovery</h6>
                  <ul>
                    <li v-for="(tip, i) in report.energy_recovery" :key="i">{{ tip }}</li>
                  </ul>
                </div>

                <div class="recommendation-card">
                  <h6><i class="bi bi-apple me-2"></i>Nutrition Tips</h6>
                  <ul>
                    <li v-for="(tip, i) in report.nutrition_tips" :key="i">{{ tip }}</li>
                  </ul>
                </div>
              </div>

              <div class="alert alert-warning">
                <strong>{{ report.summary }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Ask AI Chat -->
        <div class="card">
          <div class="card-header bg-info text-white">
            <h6 class="mb-0"><i class="bi bi-chat-dots me-2"></i>Ask AI</h6>
          </div>
          <div class="card-body">
            <div class="chat-history mb-3" style="max-height: 300px; overflow-y: auto;">
              <div v-if="chatMessages.length === 0" class="text-center text-muted py-4">
                <p>Ask me about your health!</p>
              </div>
              <div v-for="(msg, i) in chatMessages" :key="i" class="mb-2">
                <div :class="msg.type === 'user' ? 'text-end' : 'text-start'">
                  <span class="chat-message-bubble" :class="msg.type === 'user' ? 'bg-primary' : 'bg-secondary'">
                    <span v-if="msg.isThinking" class="thinking-dots">
                      <span class="dot">.</span><span class="dot">.</span><span class="dot">.</span>
                    </span>
                    <span v-else>{{ msg.text }}</span>
                  </span>
                </div>
              </div>
            </div>
            <div class="input-group mb-3">
              <input
                v-model="chatInput"
                class="form-control"
                placeholder="Ask a question..."
                @keyup.enter="askAI"
              />
              <button class="btn btn-primary" @click="askAI" :disabled="askingAI">
                Send
              </button>
            </div>

            <!-- Quick Start Prompts -->
            <div class="quick-prompts">
              <div class="d-flex align-items-center mb-2">
                <i class="bi bi-lightning-charge-fill text-warning me-2"></i>
                <small class="text-muted fw-semibold">Quick Start Prompts</small>
              </div>
              <div class="d-flex flex-wrap gap-2">
                <button
                  v-for="(prompt, index) in quickPrompts"
                  :key="index"
                  class="btn btn-sm btn-outline-info prompt-button"
                  @click="useQuickPrompt(prompt)"
                  :disabled="askingAI"
                >
                  <i :class="prompt.icon" class="me-1"></i>
                  {{ prompt.text }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
        <!-- Quick Stats -->
        <div class="card mb-3">
          <div class="card-header bg-gradient-primary text-white">
            <h6 class="mb-0"><i class="bi bi-graph-up me-2"></i>Quick Stats</h6>
          </div>
          <div class="card-body">
            <div class="stat-item mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span><i class="bi bi-calendar-heart text-danger me-2"></i>Current Phase</span>
                <strong class="text-danger">{{ currentPhase }}</strong>
              </div>
            </div>
            <div class="stat-item mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span><i class="bi bi-activity text-success me-2"></i>This Week</span>
                <strong class="text-success">{{ weeklyExercise }}</strong>
              </div>
            </div>
            <div class="stat-item">
              <div class="d-flex justify-content-between align-items-center">
                <span><i class="bi bi-robot text-info me-2"></i>AI Reports</span>
                <strong class="text-info">{{ aiReportCount }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Health Tips -->
        <div class="card">
          <div class="card-header bg-light">
            <h6 class="mb-0"><i class="bi bi-lightbulb me-2"></i>Daily Tip</h6>
          </div>
          <div class="card-body">
            <p class="mb-0 text-muted small">{{ dailyTip }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import aiService from '@/services/aiService'

export default {
  name: 'AIAnalysisReport',
  setup() {
    const { user, isAuthenticated, initAuth } = useFirebaseAuth()
    return { user, isAuthenticated, initAuth }
  },
  data() {
    return {
      report: null,
      generating: false,
      chatInput: '',
      chatMessages: [],
      askingAI: false,
      currentPhase: 'Follicular',
      weeklyExercise: '3 sessions',
      aiReportCount: 0,
      dailyTips: [
        'Stay hydrated! Drink at least 8 glasses of water daily.',
        'Listen to your body during your menstrual cycle.',
        'Regular exercise can help reduce period cramps.',
        'Get 7-8 hours of quality sleep each night.',
        'Include iron-rich foods in your diet during menstruation.',
        'Practice stress management through meditation or yoga.',
        'Track your symptoms to understand your cycle better.'
      ],
      quickPrompts: [
        {
          text: 'What exercises are best for my current cycle phase?',
          icon: 'bi bi-activity'
        },
        {
          text: 'How can I reduce period cramps naturally?',
          icon: 'bi bi-heart-pulse'
        },
        {
          text: 'What foods should I eat during menstruation?',
          icon: 'bi bi-egg-fried'
        },
        {
          text: 'Tips for better sleep during my period?',
          icon: 'bi bi-moon-stars'
        },
        {
          text: 'How to manage PMS symptoms?',
          icon: 'bi bi-emoji-smile'
        },
        {
          text: 'Is my cycle length normal?',
          icon: 'bi bi-calendar-heart'
        }
      ]
    }
  },
  computed: {
    dailyTip() {
      const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000)
      return this.dailyTips[dayOfYear % this.dailyTips.length]
    }
  },
  mounted() {
    this.initAuth()
  },
  methods: {
    async generateReport() {
      this.generating = true
      try {
        const userData = {
          userProfile: { age: 27, gender: 'female' },
          menstrualCycle: { phase: 'Follicular', averageCycleLength: 29, periodStartDate: '2025-10-01' },
          exerciseData: [
            { date: '2025-10-14', activity: 'Yoga', duration_min: 45, intensity: 'low' }
          ]
        }
        const result = await aiService.generateHealthReport(userData)
        if (result.success) {
          this.report = result.report
          this.aiReportCount++
          // Update current phase from report
          if (userData.menstrualCycle.phase) {
            this.currentPhase = userData.menstrualCycle.phase
          }
        }
      } catch (error) {
        console.error('Error:', error)
      } finally {
        this.generating = false
      }
    },
    async askAI() {
      if (!this.chatInput.trim()) return
      const question = this.chatInput
      this.chatInput = ''
      this.chatMessages.push({ type: 'user', text: question })

      // Add thinking message with animation
      const thinkingMsg = { type: 'ai', text: '', isThinking: true }
      this.chatMessages.push(thinkingMsg)

      this.askingAI = true
      try {
        const result = await aiService.askHealthQuestion(question, {})
        // Remove thinking message and add real answer
        const thinkingIndex = this.chatMessages.indexOf(thinkingMsg)
        if (thinkingIndex > -1) {
          this.chatMessages.splice(thinkingIndex, 1)
        }
        if (result.success) {
          this.chatMessages.push({ type: 'ai', text: result.answer, isThinking: false })
        }
      } finally {
        this.askingAI = false
      }
    },
    useQuickPrompt(prompt) {
      this.chatInput = prompt.text
      // Optionally auto-send the question
      this.askAI()
    }
  }
}
</script>

<style scoped>
.bg-gradient-primary {
  background: linear-gradient(135deg, #e91e63 0%, #d81b60 100%);
}
.analysis-card {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #e91e63;
}
.recommendation-card {
  background: white;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}
.section-title {
  font-weight: 600;
  color: #495057;
}

.stat-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.stat-item:last-child {
  border-bottom: none;
}

.chat-message-bubble {
  display: inline-block;
  max-width: 80%;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  color: white;
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  text-align: left;
}

.thinking-dots {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.thinking-dots .dot {
  animation: thinking 1.4s infinite;
  font-size: 1.5rem;
  line-height: 1;
}

.thinking-dots .dot:nth-child(1) {
  animation-delay: 0s;
}

.thinking-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.thinking-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes thinking {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-5px);
  }
}

.quick-prompts {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px dashed #dee2e6;
}

.prompt-button {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.prompt-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.prompt-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prompt-button i {
  font-size: 0.9rem;
}
</style>
